import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney } from '../finance/money';
import { useMe } from '../session/SessionProvider';

interface SundayClass {
  id: string;
  name: string;
  minAge: number | null;
  maxAge: number | null;
  enrolled: number;
  teacher: { personId: string; name: string } | null;
}
interface SundayRecord {
  classId: string;
  heldOn: string;
  present: number;
  visitors: number;
  bibles: number;
  offeringCents: number;
}
interface View {
  classes: SundayClass[];
  records: SundayRecord[];
  canManage: boolean;
  teacherOf: string[];
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** El domingo más reciente (hoy si es domingo), o el domingo anterior o igual a una fecha AAAA-MM-DD. */
function lastSunday(from?: string): string {
  const d = from ? new Date(`${from}T12:00:00`) : new Date();
  d.setDate(d.getDate() - d.getDay());
  return iso(d);
}
const ages = (c: SundayClass) => (c.minAge !== null || c.maxAge !== null ? `${c.minAge ?? '…'}${c.maxAge !== null ? ` a ${c.maxAge}` : ' en adelante'} años` : '');

/** Asambleas de Dios · Escuela Dominical: clases por edad y el registro de cada domingo. */
export function AdSundayPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [day, setDay] = useState(lastSunday());
  const monthFrom = `${day.slice(0, 7)}-01`;
  const monthTo = iso(new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)), 0));
  const base = `/multichurch/churches/${churchId}/ad/sunday`;
  const data = useAsync((signal) => (churchId ? api.get<View>(`${base}?from=${monthFrom}&to=${monthTo}`, signal) : Promise.resolve(null)), [base, monthFrom, monthTo]);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);

  if (!churchId) return <div className="page"><EmptyState title="Elige primero una iglesia en el selector de arriba." /></div>;
  const v = data.status === 'ready' ? data.data : null;
  const todays = v ? v.records.filter((r) => r.heldOn === day) : [];
  const sum = (rows: SundayRecord[], k: keyof Omit<SundayRecord, 'classId' | 'heldOn'>) => rows.reduce((n, r) => n + r[k], 0);
  const sundays = v ? [...new Set(v.records.map((r) => r.heldOn))] : [];
  const monthAvg = sundays.length ? Math.round(sundays.reduce((n, s) => n + v!.records.filter((r) => r.heldOn === s).reduce((m, r) => m + r.present + r.visitors, 0), 0) / sundays.length) : null;

  async function run(action: () => Promise<unknown>, ok?: string) {
    setMsg(null);
    try {
      await action();
      if (ok) setMsg({ tone: 'success', text: ok });
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    }
  }

  return (
    <div className="page">
      <header className="page__header">
        <p className="eyebrow">Asambleas de Dios</p>
        <h1>Escuela Dominical</h1>
        <p className="muted">Clases por edad. Cada domingo, el maestro o el secretario registra presentes, visitas, biblias y ofrenda de su clase.</p>
        <p className="small">
          Aquí va el conteo que pide el distrito cada domingo. Las clases, tareas y alumnos de cada salón van en <Link to="/app/salones">Raíces</Link>
        </p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {v ? (
        <>
          <div className="actions actions--wrap">
            {/* Solo domingos: el paso de 7 días parte de un domingo y, si el navegador deja escribir otro día, se ajusta al domingo anterior. */}
            <Field label="Domingo" name="day" type="date" value={day} min="2000-01-02" step={7} max={lastSunday()} onChange={(e) => e.target.value && setDay(lastSunday(e.target.value))} />
          </div>

          {v.classes.length === 0 ? <EmptyState title="Todavía no hay clases. Créalas abajo." /> : null}
          {v.classes.length ? (
            <Card title={`Registro del ${new Date(`${day}T12:00:00`).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' })}`}>
              <SundayForm
                key={day}
                classes={v.canManage || v.teacherOf.length === 0 ? v.classes : v.classes.filter((c) => v.teacherOf.includes(c.id))}
                records={todays}
                editable={(c) => v.canManage || v.teacherOf.includes(c.id)}
                onSave={(rows) =>
                  run(
                    () => Promise.all(rows.map(([c, body]) => api.put(`${base}/classes/${c.id}/records/${day}`, body))),
                    rows.length === 1 ? `Guardado el domingo: ${rows[0]![0].name}.` : `Guardado el domingo: ${rows.length} clases.`,
                  )
                }
              />
              <p className="sunday-total">
                <strong>Total del domingo:</strong> {sum(todays, 'present')} presentes · {sum(todays, 'visitors')} visitas · {sum(todays, 'bibles')} biblias ·{' '}
                {formatMoney(sum(todays, 'offeringCents'), 'MXN')} de ofrenda · matrícula {v.classes.reduce((n, c) => n + c.enrolled, 0)}
              </p>
            </Card>
          ) : null}

          <Card title="En el mes">
            <p>
              {sundays.length} {sundays.length === 1 ? 'domingo registrado' : 'domingos registrados'}
              {monthAvg !== null ? ` · asistencia promedio ${monthAvg}` : ''} · ofrenda {formatMoney(sum(v.records, 'offeringCents'), 'MXN')}
            </p>
          </Card>

          {v.canManage ? <Classes base={base} v={v} churchId={churchId} run={run} /> : null}
        </>
      ) : null}
    </div>
  );
}

type Counts = Omit<SundayRecord, 'classId' | 'heldOn'>;
const FIELDS = [
  ['present', 'Presentes'],
  ['visitors', 'Visitas'],
  ['bibles', 'Biblias'],
  ['offering', 'Ofrenda $'],
] as const;
type Draft = Record<(typeof FIELDS)[number][0], string>;
const draftOf = (r: SundayRecord | undefined): Draft =>
  r ? { present: String(r.present), visitors: String(r.visitors), bibles: String(r.bibles), offering: (r.offeringCents / 100).toFixed(2) } : { present: '', visitors: '', bibles: '', offering: '' };

/** Todas las clases del domingo con un solo botón para guardarlas a la vez. */
function SundayForm({ classes, records, editable, onSave }: { classes: SundayClass[]; records: SundayRecord[]; editable: (c: SundayClass) => boolean; onSave: (rows: [SundayClass, Counts][]) => Promise<void> }) {
  const [drafts, setDrafts] = useState<Record<string, Draft>>(() => Object.fromEntries(classes.map((c) => [c.id, draftOf(records.find((r) => r.classId === c.id))])));
  const [busy, setBusy] = useState(false);
  const mine = classes.filter(editable);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const n = (t: string) => Math.max(0, Math.round(Number(t || 0)));
    // Se guardan las clases con algo escrito y las que ya tenían registro (para corregirlas).
    const rows = mine
      .filter((c) => records.some((r) => r.classId === c.id) || Object.values(drafts[c.id]!).some((t) => t.trim() !== ''))
      .map((c): [SundayClass, Counts] => {
        const d = drafts[c.id]!;
        return [c, { present: n(d.present), visitors: n(d.visitors), bibles: n(d.bibles), offeringCents: Math.max(0, Math.round(Number(d.offering || 0) * 100)) }];
      });
    if (rows.length === 0) return;
    setBusy(true);
    await onSave(rows);
    setBusy(false);
  }

  return (
    <form onSubmit={save} aria-label="Registro del domingo">
      <ul className="sunday-rows">
        {classes.map((c) => {
          const record = records.find((r) => r.classId === c.id);
          return (
            <li key={c.id} className="sunday-row">
              <div>
                <strong>{c.name}</strong>
                <span className="small muted">{[ages(c), c.teacher ? `Maestro: ${c.teacher.name}` : null, `Matrícula ${c.enrolled}`].filter(Boolean).join(' · ')}</span>
              </div>
              {editable(c) ? (
                <div className="sunday-row__form" role="group" aria-label={`Registro de ${c.name}`}>
                  {FIELDS.map(([k, label]) => (
                    <Field
                      key={k}
                      label={label}
                      name={`${c.id}-${k}`}
                      type="number"
                      min={0}
                      step={k === 'offering' ? '0.01' : undefined}
                      value={drafts[c.id]![k]}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDrafts((d) => ({ ...d, [c.id]: { ...d[c.id]!, [k]: value } }));
                      }}
                    />
                  ))}
                </div>
              ) : (
                <span className="small">{record ? `${record.present} presentes · ${record.visitors} visitas · ${record.bibles} biblias · ${formatMoney(record.offeringCents, 'MXN')}` : 'Sin registro'}</span>
              )}
            </li>
          );
        })}
      </ul>
      {mine.length ? (
        <div className="actions">
          <Button type="submit" busy={busy}>
            Guardar el domingo
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function Classes({ base, v, churchId, run }: { base: string; v: View; churchId: string; run: (a: () => Promise<unknown>, ok?: string) => Promise<void> }) {
  const people = useAsync(
    (signal) => api.get<{ items: { personId: string; givenName: string; familyName: string }[] }>(`/multichurch/churches/${churchId}/members`, signal).then((r) => r.items).catch(() => []),
    [churchId],
  );
  function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const n = (k: string) => (String(f.get(k) ?? '').trim() ? Number(f.get(k)) : null);
    const body = { name: String(f.get('name') ?? '').trim(), minAge: n('minAge'), maxAge: n('maxAge'), enrolled: n('enrolled') ?? 0, teacherId: String(f.get('teacherId') ?? '') || null };
    e.currentTarget.reset();
    void run(() => api.post(`${base}/classes`, body), `Clase «${body.name}» creada.`);
  }
  return (
    <Card title="Clases">
      <ul className="list">
        {v.classes.map((c) => (
          <li key={c.id} className="list__item list__item--actions">
            <span>
              <strong>{c.name}</strong> <span className="small muted">{[ages(c), c.teacher?.name].filter(Boolean).join(' · ')}</span>
            </span>
            <Button variant="ghost" onClick={() => window.confirm(`¿Quitar la clase ${c.name}?`) && void run(() => api.delete(`${base}/classes/${c.id}`))}>
              Quitar
            </Button>
          </li>
        ))}
      </ul>
      <form className="form" onSubmit={add}>
        <p className="small muted">Por ejemplo: Cuna, Principiantes, Primarios, Intermedios, Adolescentes, Jóvenes, Adultos.</p>
        <Field label="Nombre de la clase" name="name" required minLength={2} maxLength={80} />
        <div className="form__row">
          <Field label="Edad desde" name="minAge" type="number" min={0} max={120} />
          <Field label="Edad hasta" name="maxAge" type="number" min={0} max={120} />
          <Field label="Matrícula" name="enrolled" type="number" min={0} />
        </div>
        <div className="field">
          <label htmlFor="class-teacher">Maestro</label>
          <select id="class-teacher" name="teacherId" defaultValue="">
            <option value="">Sin asignar</option>
            {(people.status === 'ready' ? people.data : [])
              .map((p) => ({ id: p.personId, name: `${p.givenName} ${p.familyName}`.trim() }))
              .sort((a, b) => a.name.localeCompare(b.name, 'es'))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </div>
        <Button type="submit" variant="secondary">
          Agregar clase
        </Button>
      </form>
    </Card>
  );
}
